import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Analytics from '@ublend-npm/aula-analytics';
import {
  AssessmentItem as AssessmentItemType,
  Assignment as AssignmentType,
} from '$variables/types';

import Modal from '$components/Modal';
import TextField from '$components/TextField';
import ErrorMessage from '$components/ErrorMessage';
import {
  SAVE_ASSIGNMENT_MAPPINGS,
  REMOVE_ASSIGNMENT_MAPPING,
  SELECT_ASSIGNMENT_MAPPING,
} from '$variables/analyticsEvents';
import { raiseToast } from '$components/Toast';
import AssignmentSelector from '$components/AssignmentSelector';
import { EDUCATOR } from '$variables/constants';
import * as TYPES from '$variables/types';

import pushAssignment from './utils/pushAssignment';
import replaceAssignment from './utils/repaceAssignment';
import removeAssignmentAndWeightage from './utils/removeAssignmentAndWeightage';
import replaceWeightage from './utils/replaceWeightage';
import checkSaveDisabled from './utils/checkSaveDisabled';
import filterAssignmentOptions from './utils/filterAssignmentOptions';
import validateAssignments from './utils/validateAssignments';
import getSelectedGradeType from './utils/getSelectedGradeType';
import isArrayEmpty from './utils/isArrayEmpty';
import getAssignmentDetails from './utils/getAssignmentDetails';

import AddAssignmentButton from './components/AddAssignmentButton';

import {
  ContentContainer,
  Divider,
  Subtitle,
  SecondaryTitleRow,
  Container,
  WarningMessage,
  ReadOnlyWarning,
  Description,
} from './AssignmentMappingsEditModal.styled';

const AssignmentMappingsEditModal = ({
  open,
  handleClose,
  handleSave,
  assignments,
  assessmentItem: { title, id, mappedAssignments },
  isLoading,
  error,
  isSuccessful,
  viewAs,
  isExportInProgress,
}) => {
  useEffect(() => {
    if (isSuccessful) {
      raiseToast({ message: 'Changes saved!' });
      handleClose();
    }
  }, [isSuccessful]);

  const [selectedAssignments, setSelectedAssignments] = useState(
    mappedAssignments || []
  );

  // on page load, empty selector only show when there is no mapped assignment
  const [shouldShowEmptySelector, toggleEmptySelector] = useState(
    isArrayEmpty(mappedAssignments)
  );

  /**
   * -----------------
   * Handlers
   * -----------------
   */

  const showEmptySelector = () => toggleEmptySelector(true);
  const hideEmptySelector = () => toggleEmptySelector(false);

  // handle selecting another assignment
  const handleChange = (selectorIndex) => (_, assignmentId) => {
    setSelectedAssignments(
      replaceAssignment({ selectedAssignments, assignmentId, selectorIndex })
    );

    Analytics.track({
      event: SELECT_ASSIGNMENT_MAPPING,
      assessmentItemId: id,
      assignmentId,
    });
  };

  // handle adding an assignment to mapping
  const handleAdd = (_, assignmentId) => {
    setSelectedAssignments(
      pushAssignment({ selectedAssignments, assignmentId })
    );

    hideEmptySelector();

    Analytics.track({
      event: SELECT_ASSIGNMENT_MAPPING,
      assessmentItemId: id,
      assignmentId,
    });
  };

  // handle removing an assignment from mapping
  const handleDeselect = (selectorIndex) => () => {
    const updatedSelectedAssignments = removeAssignmentAndWeightage({
      selectedAssignments,
      selectorIndex,
    });

    setSelectedAssignments(updatedSelectedAssignments);

    // if there is no more assignment
    // - display empty selector
    if (isArrayEmpty(updatedSelectedAssignments)) {
      showEmptySelector();
    }

    Analytics.track({
      event: REMOVE_ASSIGNMENT_MAPPING,
      assessmentItemId: id,
    });
  };

  // handle changing weight
  const handleWeightChange =
    (selectorIndex) =>
    ({ target: { value } }) => {
      const parsedWeight = value === '' ? undefined : parseInt(value, 10);

      const isWeightValid =
        typeof parsedWeight === 'undefined' ||
        (parsedWeight > 0 && parsedWeight < 100);

      if (isWeightValid) {
        setSelectedAssignments(
          replaceWeightage({
            selectedAssignments,
            weightage: parsedWeight,
            selectorIndex,
          })
        );
      }
    };

  const handleSubmit = async () => {
    await handleSave({
      assignmentMappings: selectedAssignments,
      assessmentItemId: id,
    });

    Analytics.track({
      event: SAVE_ASSIGNMENT_MAPPINGS,
      assessmentItemId: id,
      totalAssignmentMappings: selectedAssignments.length,
    });
  };

  /**
   * -----------------
   * Variables
   * -----------------
   */

  const isReadOnly = viewAs !== EDUCATOR || isExportInProgress;

  // extract details of mapped assignments
  const selectedAssignmentDetails = getAssignmentDetails({
    selectedAssignments,
    assignments,
  });

  const selectedGradeType = getSelectedGradeType({
    selectedAssignmentDetails,
  });

  // filter assignments based on the first selected assignment's grade type
  // e.g. if you selected a percentage assignment first,
  // the list to add second assignment should disable non-percentage assignments
  // and list the percentage ones at the top.
  // selected assignments also be disabled
  const selectOptions = filterAssignmentOptions({
    selectedAssignments,
    selectedGradeType,
    assignments,
    isOnlySelector:
      (selectedAssignments || []).length === 1 && !shouldShowEmptySelector,
  });

  // add assignment button in map assignments modal should appear
  // after the first assignment is selected.
  const shouldShowAddAssignmentButton =
    selectedAssignments.length > 0 && !isReadOnly;

  const { isAssignmentButtonDisabled, isMultiple, valid } = validateAssignments(
    {
      selectedAssignments,
      selectedAssignmentDetails,
    }
  );

  const isSaveButtonDisabled =
    isLoading ||
    checkSaveDisabled({
      selectedAssignments,
      mappedAssignments,
    }) ||
    !valid;

  return (
    <Modal
      headerText="Assessment item"
      open={open}
      handleClose={handleClose}
      submitText="Save"
      isDisabled={isSaveButtonDisabled}
      isLoading={isLoading}
      footerDecoration={
        error && (
          <ErrorMessage message="We were unable to complete the mapping." />
        )
      }
      handleSubmit={handleSubmit}
    >
      <ContentContainer>
        <TextField
          variant="outlined"
          label="Assessment item title"
          defaultValue={title}
          disabled
          margin="dense"
          fullWidth
        />
        <Divider />
        {viewAs === EDUCATOR && isExportInProgress && (
          <ReadOnlyWarning>
            <WarningMessage>
              An export for this assessment item is in progress, mapping
              settings are read-only until the export completes.
            </WarningMessage>
          </ReadOnlyWarning>
        )}
        <SecondaryTitleRow>
          <Subtitle>Composed of:</Subtitle>
          <Description>
            If the assignments you select are graded with percentages, you will
            be able to combine the grades of multiple assignments.
          </Description>
        </SecondaryTitleRow>
        {selectedAssignments.map((selectedAssignment, selectorIndex) => (
          <Container key={selectedAssignment.id}>
            <AssignmentSelector
              selectOptions={selectOptions}
              selectedAssignment={selectedAssignment}
              onSelectorChange={handleChange(selectorIndex)}
              onDeselect={handleDeselect(selectorIndex)}
              onWeightChange={handleWeightChange(selectorIndex)}
              isMultiple={isMultiple}
              disabled={isReadOnly}
            />
          </Container>
        ))}
        {shouldShowEmptySelector && (
          <Container>
            <AssignmentSelector
              selectOptions={selectOptions}
              onSelectorChange={handleAdd}
              disabled={isReadOnly}
            />
          </Container>
        )}
        {!valid && isMultiple && (
          <Container>
            <WarningMessage>
              All weights are required, and the total weight should be 100%
            </WarningMessage>
          </Container>
        )}
        <AddAssignmentButton
          onClick={showEmptySelector}
          shouldShowAddAssignmentButton={shouldShowAddAssignmentButton}
          isAssignmentButtonDisabled={isAssignmentButtonDisabled}
        />
      </ContentContainer>
    </Modal>
  );
};

AssignmentMappingsEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  assignments: PropTypes.arrayOf(AssignmentType).isRequired,
  assessmentItem: AssessmentItemType.isRequired,
  isLoading: PropTypes.bool,
  isSuccessful: PropTypes.bool.isRequired,
  error: PropTypes.string,
  viewAs: TYPES.ViewAs,
  isExportInProgress: PropTypes.bool,
};

AssignmentMappingsEditModal.defaultProps = {
  error: null,
  viewAs: EDUCATOR,
  isExportInProgress: false,
  isLoading: false,
};

export default AssignmentMappingsEditModal;
